import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useAutoDocExtSuggestionContext,
  getSingleExtraction,
} from '../AutoDocExtSuggestionProvider';
import {
  ExtractionTool,
  trackQuoteClicked,
  trackQuoteSearched,
  trackQuoteDisplayed,
  trackQuoteSearchError,
  QuoteTrackingData,
  QuoteOpenTrigger,
} from '../tracking';
import Quote from './Quote';
import styles from './QuotesSection.module.scss';
import { SuggestionStatus } from 'concepts/AutoDocExtraction/suggestionStatusStorage';
import { useDocumentViewerContext } from 'contexts/DocumentViewerProvider';
import {
  createToast,
  dismissToast,
  Icon,
  IconList,
  Toast,
} from 'components/core';
import { Stack } from 'components/layout';
import { useUniqueID } from 'hooks/useAccessibleName';
import useDisclosure from 'hooks/useDisclosure';

// Nutrient viewers TEXT search appears to expand each character into a regex group, which
// can exceed browser JS engine limits on very long quotes and throw an error.
// If that happens we retry with a truncated quote as a workaround.
const MAX_SEARCH_QUOTE_LENGTH = 1500;

export type QuotesSectionProps = {
  autoDocExtField: string;
  trackedField: string;
  suggestionStatus: SuggestionStatus;
  studyId: number;
  extractionTool: ExtractionTool;
};

export const QuotesSection = ({
  autoDocExtField,
  trackedField,
  suggestionStatus,
  studyId,
  extractionTool,
}: QuotesSectionProps) => {
  const {
    useSuggestions,
    documentId,
    allSuggestions,
  } = useAutoDocExtSuggestionContext();
  const autoDocumentExtraction = getSingleExtraction(
    allSuggestions,
    autoDocExtField
  );

  const { isLoading, searchDocument, clearSearch } = useDocumentViewerContext();

  const disclosureId = useUniqueID({ postfix: 'quote-disclosure' });
  const { isOpen, toggle, triggerProps } = useDisclosure(false, disclosureId);
  const [selectedQuoteIndex, setSelectedQuoteIndex] = useState<number | null>(
    null
  );
  const [quoteToSearch, setQuoteToSearch] = useState<string | null>(null);

  const prevIsOpenRef = useRef<boolean>(false);
  const triggerRef = useRef<QuoteOpenTrigger>('auto');

  // Deselect the selected quote when the document changes, and reset the
  // open-tracking ref so it fires its own `suggestion_quote_displayed` event.
  useEffect(() => {
    setSelectedQuoteIndex(null);
    setQuoteToSearch(null);
    prevIsOpenRef.current = false;
    triggerRef.current = 'auto';
  }, [documentId]);

  useEffect(() => {
    if (!isOpen) {
      clearSearch();
      setSelectedQuoteIndex(null);
      setQuoteToSearch(null);
    }
  }, [isOpen, clearSearch]);

  const quotes = autoDocumentExtraction?.quotes;

  const eventData: QuoteTrackingData = useMemo(
    () => ({
      field: trackedField,
      quotesCount: quotes?.length ?? 0,
      documentId,
      reviewStudyId: studyId,
      suggestionStatus,
      extractionTool,
    }),
    [
      trackedField,
      documentId,
      quotes?.length,
      studyId,
      suggestionStatus,
      extractionTool,
    ]
  );

  useEffect(() => {
    if (isLoading || !quoteToSearch) return;

    let cancelled = false;

    const searchQuote = async () => {
      const runSearch = async (quote: string) => {
        const result = await searchDocument(quote);
        trackQuoteSearched({ ...eventData, resultsCount: result, quote });

        if (result === 0 && !cancelled) {
          setSelectedQuoteIndex(null);
          createToast(
            <Toast type="danger" role="alert">
              Couldn&apos;t navigate to this quote - search the PDF text
              directly
            </Toast>
          );
        }
      };

      clearSearch();
      dismissToast();
      try {
        await runSearch(quoteToSearch);
      } catch {
        await runSearch(quoteToSearch.slice(0, MAX_SEARCH_QUOTE_LENGTH)).catch(
          () => {
            trackQuoteSearchError({
              ...eventData,
              resultsCount: 0,
              quote: quoteToSearch,
            });
            if (!cancelled) {
              setSelectedQuoteIndex(null);
              createToast(
                <Toast type="danger" role="alert">
                  Something went wrong while searching for the quote. Try again
                  later.
                </Toast>
              );
            }
          }
        );
      } finally {
        if (!cancelled) {
          setQuoteToSearch(null);
        }
      }
    };

    searchQuote();

    return () => {
      cancelled = true;
    };
  }, [
    isLoading,
    quoteToSearch,
    searchDocument,
    eventData,
    clearSearch,
    dismissToast,
  ]);

  // Fire trackQuoteDisplayed on every false→true transition of isOpen. The
  // previous-value ref avoids re-firing when other deps (eventData,
  // suggestionStatus) change while the panel is already open. The ref is
  // also reset on document change (above), so a doc switch with the
  // panel still auto-open fires a fresh event for the new document.
  //
  // We early-return until quotes have actually loaded so the
  // open-with-zero-quotes initial render doesn't fire a phantom event;
  // the early return leaves prevIsOpenRef untouched, so the real open
  // transition still fires once the data arrives.
  useEffect(() => {
    if (!quotes?.length) return;
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (isOpen && !wasOpen) {
      trackQuoteDisplayed({
        ...eventData,
        trigger: triggerRef.current,
      });
    }
  }, [isOpen, eventData, quotes?.length]);

  const onQuoteClick = useCallback(
    (quote: string, index: number) => {
      setSelectedQuoteIndex(index);
      trackQuoteClicked(eventData);
      setQuoteToSearch(quote);
    },
    [eventData]
  );

  const handleToggle = useCallback(() => {
    triggerRef.current = 'button';
    toggle();
  }, [toggle]);

  if (!useSuggestions || !quotes?.length) return null;

  const quoteCount = quotes.length;
  const toggleLabel =
    quoteCount === 1 ? '1 quote found' : `${quoteCount} quotes found`;

  return (
    <div>
      <button
        {...triggerProps}
        type="button"
        className={styles.quotesToggleButton}
        onClick={handleToggle}
      >
        {toggleLabel}
        <Icon
          size="sm"
          icon={isOpen ? IconList.light.faAngleUp : IconList.light.faAngleDown}
        />
      </button>
      <div id={disclosureId}>
        {isOpen && (
          <Stack spacing={3}>
            {quotes.map((quote, index) => (
              <Quote
                key={`${index}-${quote}`}
                quote={quote}
                index={index}
                selected={selectedQuoteIndex === index}
                onClick={onQuoteClick}
              />
            ))}
          </Stack>
        )}
      </div>
    </div>
  );
};
